import { useCallback, useEffect, useState } from "react";
import { useApi } from "../api/useApi";
import { uploadedDataSourcesByName } from "../api/dataSourceQueries";

/**
 * Finds an existing uploaded source that a file would collide with.
 *
 * An uploaded source is named after its file, so uploading loans.csv when a source of
 * that name already exists would produce a second one rather than loading the new data
 * into the first. The server refuses that, but only once the file has been sent and the
 * source is being created. Asking first means the choice — replace the existing file, or
 * pick a different file — is offered before anything is uploaded.
 *
 * Only sources the user can see are considered, because that is all the query returns and
 * all the server refuses on. A name held by a source they have no access to is not a
 * collision they could do anything about.
 */
const useUploadedSourceCollision = () => {
  const [{ data }, findByName] = useApi();
  const [candidateName, setCandidateName] = useState(null);
  const [collision, setCollision] = useState(null);

  /** Starts a check for `fileName`; the result arrives as `collision`. */
  const checkForCollision = useCallback(
    (fileName) => {
      setCollision(null);
      setCandidateName(fileName ?? null);
      if (!fileName) return;

      findByName({
        query: uploadedDataSourcesByName,
        variables: {
          // Required by the query and irrelevant here: nothing is being filtered by tag.
          tagFilter: { tagIds: [], exactMatch: false },
          where: { name: { contains: fileName } },
        },
      });
    },
    [findByName]
  );

  const clearCollision = useCallback(() => {
    setCollision(null);
    setCandidateName(null);
  }, []);

  useEffect(() => {
    if (!candidateName || !data) return;

    // `contains` is a loose match by design — it is the only case-insensitive string
    // filter the API offers — so the exact comparison happens here. Case-insensitive to
    // match the server's own refusal, which lowercases both sides.
    const wanted = candidateName.toLowerCase();
    const match = (data.availableDataSources?.edges ?? [])
      .map((edge) => edge?.node)
      .find(
        (node) =>
          node?.connection?.serverType === "LOCAL_FILE_UPLOAD" &&
          node?.name?.toLowerCase() === wanted
      );

    setCollision(match ? { id: match.id, name: match.name } : null);
  }, [data, candidateName]);

  return { collision, checkForCollision, clearCollision };
};

export default useUploadedSourceCollision;
